import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Briefcase, CalendarDays, CheckCircle, ExternalLink, Loader2 } from 'lucide-react';
import { getAuthHeaders } from '../../../contexts/AuthContext';
import { API } from '../execShared';

export default function MyCampaignsSection() {
  const [participations, setParticipations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitModal, setSubmitModal] = useState(null);
  const [submitStep, setSubmitStep] = useState(1);
  const [submitData, setSubmitData] = useState({ platform: '', post_url: '', screenshot_url: '' });
  const [submitting, setSubmitting] = useState(false);
  const [scheduleModal, setScheduleModal] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [scheduling, setScheduling] = useState(false);
  // Gig Economy Phase 1: testimonial + survey state
  const [testimonialModal, setTestimonialModal] = useState(null);
  const [testimonialText, setTestimonialText] = useState('');
  const [testimonialBusy, setTestimonialBusy] = useState(false);
  const [surveyModal, setSurveyModal] = useState(null); // { participation, survey }
  const [surveyAnswers, setSurveyAnswers] = useState({});
  const [surveyBusy, setSurveyBusy] = useState(false);
  // Phase 2: video / sessions / projects state
  const [videoModal, setVideoModal] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoBusy, setVideoBusy] = useState(false);
  const [sessionModal, setSessionModal] = useState(null);
  const [sessionSlots, setSessionSlots] = useState(['', '', '']);
  const [sessionBusy, setSessionBusy] = useState(false);
  const [sessionNotesModal, setSessionNotesModal] = useState(null); // { participation, session_id }
  const [sessionNotes, setSessionNotes] = useState('');
  const [projectModal, setProjectModal] = useState(null); // { participation, project }
  const [milestoneSubmit, setMilestoneSubmit] = useState({}); // {milestone_id: {url, notes}}
  const [projectBusy, setProjectBusy] = useState('');
  // Phase 4: AI Training Q&A
  const [aiTrainingModal, setAiTrainingModal] = useState(null); // { participation, aitr }
  const [aiTrainingAnswers, setAiTrainingAnswers] = useState({});
  const [aiTrainingBusy, setAiTrainingBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/my/campaigns`, { headers: getAuthHeaders() }).then(r => setParticipations(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const refresh = async () => {
    const { data } = await axios.get(`${API}/my/campaigns`, { headers: getAuthHeaders() });
    setParticipations(data);
  };

  const handleSubmitPost = async () => {
    if (!submitModal) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/submissions/${submitModal.id}/submit`, submitData, { headers: getAuthHeaders() });
      setSubmitModal(null);
      setSubmitStep(1);
      setSubmitData({ platform: '', post_url: '', screenshot_url: '' });
      await refresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  const handleAccept = async (partId) => {
    try {
      await axios.post(`${API}/my/campaigns/${partId}/accept`, {}, { headers: getAuthHeaders() });
      await refresh();
      // Only auto-open the scheduling modal for endorsement posts.
      const updated = (await axios.get(`${API}/my/campaigns`, { headers: getAuthHeaders() })).data;
      const part = updated.find(p => p.id === partId);
      const ot = part?.campaign?.offering_type || 'endorsement_post';
      if (part && ot === 'endorsement_post') setScheduleModal(part);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to accept');
    }
  };

  // Testimonial: open modal & submit
  const openTestimonialModal = async (p) => {
    setTestimonialModal(p);
    setTestimonialText('');
    // Load any saved draft
    try {
      const { data } = await axios.get(`${API}/testimonials/draft/${p.id}`, { headers: getAuthHeaders() });
      if (data?.text) setTestimonialText(data.text);
    } catch { /* no draft */ }
  };

  // Auto-save draft (debounced)
  useEffect(() => {
    if (!testimonialModal || !testimonialText.trim()) return;
    const t = setTimeout(() => {
      axios.post(`${API}/testimonials/draft`, {
        participant_id: testimonialModal.id, text: testimonialText,
      }, { headers: getAuthHeaders() }).catch(() => {});
    }, 1200);
    return () => clearTimeout(t);
  }, [testimonialText, testimonialModal]);

  const submitTestimonial = async () => {
    if (!testimonialModal) return;
    const wc = testimonialText.trim().split(/\s+/).filter(Boolean).length;
    if (wc < 30) { toast.error('Testimonial is too short (minimum 30 words).'); return; }
    if (wc > 300) { toast.error('Testimonial is too long (maximum 300 words).'); return; }
    setTestimonialBusy(true);
    try {
      await axios.post(`${API}/testimonials`, {
        campaign_id: testimonialModal.campaign_id,
        participant_id: testimonialModal.id,
        text: testimonialText.trim(),
      }, { headers: getAuthHeaders() });
      setTestimonialModal(null);
      setTestimonialText('');
      toast.success('Testimonial submitted for brand review.');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit testimonial');
    } finally { setTestimonialBusy(false); }
  };

  // Survey: open modal (fetches questions) & submit
  const openSurveyModal = async (p) => {
    try {
      const { data } = await axios.get(`${API}/surveys/by-campaign/${p.campaign_id}`, { headers: getAuthHeaders() });
      setSurveyModal({ participation: p, survey: data });
      setSurveyAnswers({});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Survey not found');
    }
  };

  const submitSurvey = async (externalCompleted = false) => {
    if (!surveyModal) return;
    setSurveyBusy(true);
    try {
      await axios.post(`${API}/surveys/${surveyModal.survey.id}/respond`, {
        participant_id: surveyModal.participation.id,
        answers: surveyAnswers,
        external_completed: externalCompleted,
      }, { headers: getAuthHeaders() });
      setSurveyModal(null);
      setSurveyAnswers({});
      toast.success('Survey submitted. Thanks!');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit survey');
    } finally { setSurveyBusy(false); }
  };

  // ===== Phase 2 handlers =====
  const aiStarter = async (campaignId) => {
    try {
      const { data } = await axios.post(`${API}/ai/testimonial-starter`, { campaign_id: campaignId }, { headers: getAuthHeaders() });
      if (data.starter) { setTestimonialText(data.starter); toast.success('AI starter loaded — edit and personalize'); }
      else { toast.info('AI starter is unavailable right now — please draft from scratch.'); }
    } catch (e) { toast.error(e.response?.data?.detail || 'AI helper failed'); }
  };

  const aiSurveyStarter = async (surveyId, questionId) => {
    try {
      const { data } = await axios.post(`${API}/ai/survey-answer-starter`, { survey_id: surveyId, question_id: questionId }, { headers: getAuthHeaders() });
      if (data.starter) {
        setSurveyAnswers(prev => ({ ...prev, [questionId]: data.starter }));
        toast.success('AI starter loaded — edit before submitting');
      } else { toast.info('AI starter unavailable — please answer manually.'); }
    } catch (e) { toast.error(e.response?.data?.detail || 'AI helper failed'); }
  };

  const openVideoModal = (p) => { setVideoModal(p); setVideoUrl(''); };

  const submitVideo = async () => {
    if (!videoModal) return;
    if (!videoUrl.trim()) { toast.error('Upload a video first'); return; }
    setVideoBusy(true);
    try {
      await axios.post(`${API}/video-testimonials`, {
        campaign_id: videoModal.campaign_id, participant_id: videoModal.id, video_url: videoUrl.trim(),
      }, { headers: getAuthHeaders() });
      setVideoModal(null); setVideoUrl('');
      toast.success('Video submitted for review');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit'); }
    finally { setVideoBusy(false); }
  };

  const openSessionModal = (p) => { setSessionModal(p); setSessionSlots(['', '', '']); };

  const submitSessionSlots = async () => {
    if (!sessionModal) return;
    const slots = sessionSlots.filter(s => !!s);
    if (slots.length < 1) { toast.error('Propose at least one time slot'); return; }
    setSessionBusy(true);
    try {
      await axios.post(`${API}/sessions/propose-times`, {
        participant_id: sessionModal.id, slots: slots.map(s => new Date(s).toISOString()),
      }, { headers: getAuthHeaders() });
      setSessionModal(null); setSessionSlots(['', '', '']);
      toast.success('Time slots proposed — brand will confirm one.');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit slots'); }
    finally { setSessionBusy(false); }
  };

  const submitSessionComplete = async () => {
    if (!sessionNotesModal) return;
    if (sessionNotes.trim().length < 30) { toast.error('Notes must be at least 30 characters'); return; }
    setSessionBusy(true);
    try {
      await axios.post(`${API}/sessions/${sessionNotesModal.session_id}/complete`, { notes: sessionNotes.trim() }, { headers: getAuthHeaders() });
      setSessionNotesModal(null); setSessionNotes('');
      toast.success('Session marked complete');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSessionBusy(false); }
  };

  const openProjectModal = async (p) => {
    try {
      const { data } = await axios.get(`${API}/projects/by-campaign/${p.campaign_id}`, { headers: getAuthHeaders() });
      setProjectModal({ participation: p, project: data });
      setMilestoneSubmit({});
    } catch (e) { toast.error(e.response?.data?.detail || 'Project not defined yet'); }
  };

  const submitMilestone = async (milestoneId) => {
    if (!projectModal) return;
    const ms = milestoneSubmit[milestoneId] || {};
    if (!(ms.url || '').trim() && !(ms.notes || '').trim()) { toast.error('Provide a deliverable URL or notes'); return; }
    setProjectBusy(milestoneId);
    try {
      await axios.post(`${API}/projects/${projectModal.project.id}/milestones/${milestoneId}/submit`, {
        deliverable_url: (ms.url || '').trim(), notes: (ms.notes || '').trim(),
      }, { headers: getAuthHeaders() });
      const { data: updated } = await axios.get(`${API}/projects/by-campaign/${projectModal.participation.campaign_id}`, { headers: getAuthHeaders() });
      setProjectModal({ ...projectModal, project: updated });
      setMilestoneSubmit({ ...milestoneSubmit, [milestoneId]: { url: '', notes: '' } });
      toast.success('Milestone submitted for review');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setProjectBusy(''); }
  };

  // AI Training handlers
  const openAiTrainingModal = async (p) => {
    try {
      const { data: aitr } = await axios.get(`${API}/ai-training/by-campaign/${p.campaign_id}`, { headers: getAuthHeaders() });
      const { data: prior } = await axios.get(`${API}/ai-training/my/${aitr.id}?participant_id=${p.id}`, { headers: getAuthHeaders() });
      setAiTrainingModal({ participation: p, aitr });
      setAiTrainingAnswers(prior?.answers || {});
    } catch (e) { toast.error(e.response?.data?.detail || 'Question set not ready yet'); }
  };

  // Auto-save AI training draft (debounced)
  useEffect(() => {
    if (!aiTrainingModal) return;
    const t = setTimeout(() => {
      axios.post(`${API}/ai-training/${aiTrainingModal.aitr.id}/save-draft`, {
        participant_id: aiTrainingModal.participation.id, answers: aiTrainingAnswers,
      }, { headers: getAuthHeaders() }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [aiTrainingAnswers, aiTrainingModal]);

  const submitAiTraining = async () => {
    if (!aiTrainingModal) return;
    setAiTrainingBusy(true);
    try {
      await axios.post(`${API}/ai-training/${aiTrainingModal.aitr.id}/submit`, {
        participant_id: aiTrainingModal.participation.id, answers: aiTrainingAnswers,
      }, { headers: getAuthHeaders() });
      setAiTrainingModal(null); setAiTrainingAnswers({});
      toast.success('AI training Q&A submitted. Thanks!');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setAiTrainingBusy(false); }
  };

  const handleDecline = async (partId) => {
    if (!window.confirm('Are you sure you want to decline this campaign?')) return;
    try {
      await axios.post(`${API}/my/campaigns/${partId}/decline`, {}, { headers: getAuthHeaders() });
      await refresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to decline');
    }
  };

  const handleSchedule = async () => {
    if (!scheduleModal || !selectedDate) return;
    setScheduling(true);
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      await axios.post(`${API}/my/campaigns/${scheduleModal.id}/schedule`, {
        scheduled_date: dateStr, campaign_id: scheduleModal.campaign_id
      }, { headers: getAuthHeaders() });
      setScheduleModal(null);
      setSelectedDate(null);
      await refresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to schedule');
    } finally { setScheduling(false); }
  };

  const statusConfig = {
    invited: { color: 'text-[#FF4343]', bg: 'bg-[#FF4343]/10', label: 'Invited' },
    accepted: { color: 'text-[#10B981]', bg: 'bg-[#10B981]/10', label: 'Accepted' },
    submitted: { color: 'text-yellow-400', bg: 'bg-yellow-400/10', label: 'Pending Verification' },
    verified: { color: 'text-[#10B981]', bg: 'bg-[#10B981]/10', label: 'Verified' },
    approved: { color: 'text-[#10B981]', bg: 'bg-[#10B981]/10', label: 'Approved — Payout Processing' },
    paid: { color: 'text-[#10B981]', bg: 'bg-[#10B981]/10', label: 'Paid' },
    flagged: { color: 'text-yellow-400', bg: 'bg-yellow-400/10', label: 'Needs Review' },
    revision_requested: { color: 'text-[#FF4343]', bg: 'bg-[#FF4343]/10', label: 'Revision Requested' },
  };

  const needsScreenshot = (p) => ['linkedin', 'instagram'].includes(submitData.platform);
  const urlValid = submitData.post_url && submitData.platform;

  if (loading) return <p className="text-[#9CA3AF]">Loading campaigns...</p>;

  return (
    <div className="space-y-4" data-testid="my-campaigns-section">
      <h2 className="font-display text-2xl font-bold">My <span className="text-[#FF4343]">Campaigns</span></h2>
      {participations.length === 0 ? (
        <div className="text-center py-12 bg-[#111827] border border-white/10">
          <Briefcase className="w-10 h-10 text-[#6B7280] mx-auto mb-3" />
          <p className="text-[#9CA3AF]">No campaign participations yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {participations.map(p => {
            const s = statusConfig[p.status] || { color: 'text-[#6B7280]', bg: 'bg-white/5', label: p.status };
            return (
              <div key={p.id} className="bg-[#111827] border border-white/10 p-5" data-testid={`participation-${p.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-medium text-white">{p.campaign?.title || p.campaign_id}</h3>
                    <p className="text-[10px] text-[#6B7280] mt-0.5">{p.campaign?.brand_name} · {p.campaign?.platforms?.join(', ')}</p>
                  </div>
                  <span className={`text-[10px] font-mono uppercase px-2 py-1 ${s.color} ${s.bg}`}>{s.label}</span>
                </div>

                {/* Status-specific content */}
                {p.status === 'invited' && (
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => handleAccept(p.id)} data-testid={`accept-${p.id}`}
                      className="text-xs px-4 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold hover:bg-[#E03030] transition-all">
                      Accept Campaign
                    </button>
                    <button onClick={() => handleDecline(p.id)} data-testid={`decline-${p.id}`}
                      className="text-xs px-4 py-2 border border-white/20 text-[#9CA3AF] hover:text-white transition-colors">
                      Decline
                    </button>
                  </div>
                )}

                {p.status === 'accepted' && (() => {
                  const ot = p.campaign?.offering_type || 'endorsement_post';
                  // Written Testimonial flow
                  if (ot === 'written_testimonial') {
                    return (
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-xs text-[#9CA3AF]">Draft a 30–300 word testimonial about <strong className="text-white">{p.campaign?.brand_name}</strong>. They'll approve or request a revision.</p>
                        <button onClick={() => openTestimonialModal(p)} data-testid={`write-testimonial-btn-${p.id}`}
                          className="text-xs px-4 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold hover:bg-[#E03030] transition-all whitespace-nowrap">
                          Write Testimonial
                        </button>
                      </div>
                    );
                  }
                  // Video Testimonial flow
                  if (ot === 'video_testimonial') {
                    return (
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-xs text-[#9CA3AF]">Record a 30-90 sec selfie video for <strong className="text-white">{p.campaign?.brand_name}</strong>.</p>
                        <button onClick={() => openVideoModal(p)} data-testid={`record-video-btn-${p.id}`}
                          className="text-xs px-4 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold hover:bg-[#E03030] transition-all whitespace-nowrap">
                          Upload Video
                        </button>
                      </div>
                    );
                  }
                  // Survey flow
                  if (ot === 'survey') {
                    return (
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-xs text-[#9CA3AF]">{p.campaign?.brand_name} is collecting structured feedback. Should take 3–10 minutes.</p>
                        <button onClick={() => openSurveyModal(p)} data-testid={`take-survey-btn-${p.id}`}
                          className="text-xs px-4 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold hover:bg-[#E03030] transition-all whitespace-nowrap">
                          Take Survey
                        </button>
                      </div>
                    );
                  }
                  // Session flows (feedback_session, advisory_call, sales_pitch — all are meetings)
                  if (ot === 'feedback_session' || ot === 'advisory_call' || ot === 'sales_pitch') {
                    // After slot confirmed, exec sees join + Mark Complete
                    if (p.scheduled_date && p.session_id) {
                      return (
                        <div className="space-y-2">
                          <p className="text-xs text-[#9CA3AF]">Session confirmed on <strong className="text-white">{p.scheduled_date}</strong>.</p>
                          <button onClick={() => { setSessionNotesModal({ participation: p, session_id: p.session_id }); setSessionNotes(''); }}
                            data-testid={`complete-session-btn-${p.id}`}
                            className="text-xs px-4 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold hover:bg-[#E03030] transition-all">
                            Mark Complete &amp; Submit Notes
                          </button>
                        </div>
                      );
                    }
                    return (
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-xs text-[#9CA3AF]">{ot === 'advisory_call' ? 'Schedule a 1-on-1 expert call' : 'Schedule a product feedback session'} with {p.campaign?.brand_name}.</p>
                        <button onClick={() => openSessionModal(p)} data-testid={`propose-session-btn-${p.id}`}
                          className="text-xs px-4 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold hover:bg-[#E03030] transition-all whitespace-nowrap">
                          Propose Times
                        </button>
                      </div>
                    );
                  }
                  // Consulting Project flow
                  if (ot === 'consulting_project') {
                    return (
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-xs text-[#9CA3AF]">Multi-milestone project for <strong className="text-white">{p.campaign?.brand_name}</strong>. Submit each milestone for payout.</p>
                        <button onClick={() => openProjectModal(p)} data-testid={`open-project-btn-${p.id}`}
                          className="text-xs px-4 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold hover:bg-[#E03030] transition-all whitespace-nowrap">
                          View Project
                        </button>
                      </div>
                    );
                  }
                  // AI Training Q&A flow
                  if (ot === 'ai_training') {
                    return (
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-xs text-[#9CA3AF]">Answer <strong className="text-white">{p.campaign?.brand_name}</strong>'s questions in your voice — they use your answers to train their AI on real domain expertise.</p>
                        <button onClick={() => openAiTrainingModal(p)} data-testid={`open-ai-training-btn-${p.id}`}
                          className="text-xs px-4 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold hover:bg-[#E03030] transition-all whitespace-nowrap">
                          Train AI
                        </button>
                      </div>
                    );
                  }
                  // Default: endorsement post (existing flow)
                  return (
                    <div>
                      {p.scheduled_date ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-xs text-[#9CA3AF]">
                            <CalendarDays className="w-3.5 h-3.5 text-[#FF4343]" />
                            <span>Scheduled: <strong className="text-white">{new Date(p.scheduled_date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</strong></span>
                          </div>
                          <button onClick={() => { setSubmitModal(p); setSubmitStep(1); setSubmitData({ platform: (p.campaign?.platforms || [])[0]?.toLowerCase() || '', post_url: '', screenshot_url: '' }); }}
                            data-testid={`submit-post-btn-${p.id}`}
                            className="text-xs px-4 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold hover:bg-[#E03030] transition-all">
                            Submit Your Post
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <p className="text-xs text-[#9CA3AF]">Pick a date for your post:</p>
                          <button onClick={() => { setScheduleModal(p); setSelectedDate(null); }}
                            data-testid={`schedule-btn-${p.id}`}
                            className="text-xs px-4 py-2 border-2 border-[#FF4343] bg-transparent text-[#FF4343] hover:bg-[#FF4343] hover:text-[#090E33] font-medium transition-all flex items-center gap-1.5">
                            <CalendarDays className="w-3.5 h-3.5" /> Schedule Post Date
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {p.status === 'revision_requested' && p.campaign?.offering_type === 'written_testimonial' && (
                  <div className="space-y-2">
                    <p className="text-xs text-[#FF4343]">Brand requested a revision on your testimonial. Update and resubmit.</p>
                    <button onClick={() => openTestimonialModal(p)} data-testid={`revise-testimonial-btn-${p.id}`}
                      className="text-xs px-4 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold hover:bg-[#E03030] transition-all">
                      Revise &amp; Resubmit
                    </button>
                  </div>
                )}

                {p.status === 'submitted' && (
                  <div className="flex items-center gap-2 text-xs text-yellow-400">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Verification in progress...
                  </div>
                )}

                {(p.status === 'verified' || p.status === 'approved') && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-[#090E33] p-3"><span className="font-mono text-lg text-white">{(p.impressions || 0).toLocaleString()}</span><p className="text-[9px] text-[#6B7280] font-mono uppercase mt-0.5">Impressions</p></div>
                    {p.payout_amount && <div className="bg-[#090E33] p-3"><span className="font-mono text-lg text-[#FF4343]">${p.payout_amount.toLocaleString()}</span><p className="text-[9px] text-[#6B7280] font-mono uppercase mt-0.5">Payout</p></div>}
                  </div>
                )}

                {p.status === 'paid' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-[#090E33] p-3"><span className="font-mono text-lg text-white">{(p.impressions || 0).toLocaleString()}</span><p className="text-[9px] text-[#6B7280] font-mono uppercase mt-0.5">Impressions</p></div>
                    <div className="bg-[#090E33] p-3"><span className="font-mono text-lg text-[#10B981]">${(p.payout_amount || 0).toLocaleString()}</span><p className="text-[9px] text-[#6B7280] font-mono uppercase mt-0.5">Earned</p></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}


      {/* Schedule Date Picker Modal */}
      {scheduleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setScheduleModal(null)} />
          <div className="relative glass max-w-sm w-full p-6" data-testid="schedule-modal">
            <h3 className="text-sm font-semibold mb-1">Schedule Your Post</h3>
            <p className="text-[10px] text-[#6B7280] mb-4">{scheduleModal.campaign?.title}</p>
            <p className="text-xs text-[#9CA3AF] mb-4">Pick a date at least 48 hours from now{scheduleModal.campaign?.deadline ? ` and before ${new Date(scheduleModal.campaign.deadline.split('T')[0] + 'T12:00:00').toLocaleDateString()}` : ''}.</p>
            
            <div className="mb-4">
              <input type="date" value={selectedDate ? selectedDate.toISOString().split('T')[0] : ''}
                min={new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString().split('T')[0]}
                max={scheduleModal.campaign?.deadline ? scheduleModal.campaign.deadline.split('T')[0] : undefined}
                onChange={e => setSelectedDate(e.target.value ? new Date(e.target.value + 'T12:00:00') : null)}
                data-testid="schedule-date-input"
                className="w-full px-3 py-3 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]"
              />
            </div>

            {selectedDate && (
              <p className="text-xs text-[#10B981] mb-4 flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5" />
                {selectedDate.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            )}

            <div className="flex gap-2">
              <button onClick={handleSchedule} disabled={!selectedDate || scheduling} data-testid="confirm-schedule-btn"
                className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-5 py-2 text-sm font-semibold hover:bg-[#E03030] disabled:opacity-50 transition-all">
                {scheduling ? 'Scheduling...' : 'Confirm Date'}
              </button>
              <button onClick={() => setScheduleModal(null)} className="border border-white/20 text-[#9CA3AF] hover:text-white px-5 py-2 text-sm transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Post Submission Modal */}
      {submitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSubmitModal(null)} />
          <div className="relative glass max-w-lg w-full p-6" data-testid="post-submission-modal">
            <h3 className="text-sm font-semibold mb-1">Submit Your Post</h3>
            <p className="text-[10px] text-[#6B7280] mb-4">{submitModal.campaign?.title}</p>

            {/* Step indicator */}
            <div className="flex gap-2 mb-5">
              {['Platform', 'Post URL', needsScreenshot() ? 'Screenshot' : null, 'Confirm'].filter(Boolean).map((label, i) => (
                <div key={i} className={`flex-1 text-center text-[9px] font-mono uppercase py-1.5 border-b-2 ${submitStep >= i + 1 ? 'border-[#FF4343] text-[#FF4343]' : 'border-white/10 text-[#6B7280]'}`}>{label}</div>
              ))}
            </div>

            {/* Step 1: Platform */}
            {submitStep === 1 && (
              <div className="space-y-3" data-testid="submit-step-1">
                <p className="text-xs text-[#9CA3AF]">Confirm the platform where your post is live:</p>
                <div className="flex gap-2">
                  {['linkedin', 'x', 'instagram', 'youtube'].map(p => (
                    <button key={p} onClick={() => setSubmitData({...submitData, platform: p})} data-testid={`platform-${p}`}
                      className={`text-xs px-4 py-2.5 border capitalize ${submitData.platform === p ? 'border-[#FF4343] text-[#FF4343] bg-[#FF4343]/5' : 'border-white/10 text-[#6B7280]'}`}>{p}</button>
                  ))}
                </div>
                <button onClick={() => setSubmitStep(2)} disabled={!submitData.platform} className="text-xs px-5 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold disabled:opacity-50">Continue</button>
              </div>
            )}

            {/* Step 2: Post URL */}
            {submitStep === 2 && (
              <div className="space-y-3" data-testid="submit-step-2">
                <p className="text-xs text-[#9CA3AF]">Paste the link to your live post:</p>
                <input value={submitData.post_url} onChange={e => setSubmitData({...submitData, post_url: e.target.value})} data-testid="post-url-input"
                  placeholder={`Paste your ${submitData.platform} post URL...`}
                  className="w-full px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
                {submitData.post_url && urlValid && <div className="flex items-center gap-1.5 text-xs text-[#10B981]"><CheckCircle className="w-3.5 h-3.5" /> URL looks good</div>}
                <div className="flex gap-2">
                  <button onClick={() => setSubmitStep(1)} className="text-xs px-4 py-2 border border-white/20 text-[#9CA3AF]">Back</button>
                  <button onClick={() => setSubmitStep(needsScreenshot() ? 3 : 4)} disabled={!submitData.post_url}
                    className="text-xs px-5 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold disabled:opacity-50">Continue</button>
                </div>
              </div>
            )}

            {/* Step 3: Screenshot (LinkedIn/Instagram only) */}
            {submitStep === 3 && needsScreenshot() && (
              <div className="space-y-3" data-testid="submit-step-3">
                <div className="bg-[#090E33] border border-white/10 p-3 text-xs text-[#9CA3AF] leading-relaxed">
                  {submitData.platform === 'linkedin' ? (
                    <p>Go to your LinkedIn post. Below the post, click where it shows your impression count (e.g. "847 impressions"). This opens your analytics. Take a screenshot showing that number clearly and upload it here.</p>
                  ) : (
                    <p>Open your Instagram post. Tap "View Insights." Take a screenshot showing your reach or impressions and upload it here.</p>
                  )}
                </div>
                {/* File upload */}
                <div className="space-y-2">
                  <label className="block">
                    <input type="file" accept="image/*" onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 10 * 1024 * 1024) { alert('Max 10MB'); return; }
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const { data } = await axios.post(`${API}/upload`, formData, {
                          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
                        });
                        setSubmitData({...submitData, screenshot_url: data.serve_url});
                      } catch (err) {
                        alert(err.response?.data?.detail || 'Upload failed');
                      }
                    }} data-testid="screenshot-file-input"
                      className="block w-full text-sm text-[#6B7280] file:mr-4 file:py-2 file:px-4 file:border file:border-white/10 file:text-xs file:text-[#FF4343] file:bg-transparent hover:file:bg-[#FF4343]/10 file:cursor-pointer file:transition-colors"
                    />
                  </label>
                  {submitData.screenshot_url && (
                    <div className="flex items-center gap-2">
                      <img src={submitData.screenshot_url} alt="Screenshot preview" className="h-20 border border-white/10 object-contain" />
                      <CheckCircle className="w-4 h-4 text-[#10B981]" />
                      <span className="text-[10px] text-[#10B981]">Uploaded</span>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setSubmitStep(2)} className="text-xs px-4 py-2 border border-white/20 text-[#9CA3AF]">Back</button>
                  <button onClick={() => setSubmitStep(4)} disabled={!submitData.screenshot_url}
                    className="text-xs px-5 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold disabled:opacity-50">Continue</button>
                </div>
              </div>
            )}

            {/* Step 4: Confirm */}
            {submitStep === (needsScreenshot() ? 4 : 3) && (
              <div className="space-y-3" data-testid="submit-step-confirm">
                <div className="bg-[#090E33] border border-white/10 p-4 space-y-2 text-xs">
                  <div className="flex justify-between"><span className="text-[#6B7280]">Platform:</span><span className="text-white capitalize">{submitData.platform}</span></div>
                  <div className="flex justify-between"><span className="text-[#6B7280]">Post URL:</span><span className="text-white truncate max-w-[200px]">{submitData.post_url}</span></div>
                  {needsScreenshot() && <div className="flex justify-between"><span className="text-[#6B7280]">Screenshot:</span><span className="text-[#10B981]">Uploaded</span></div>}
                </div>
                <label className="flex items-start gap-2 text-xs text-[#9CA3AF] cursor-pointer">
                  <input type="checkbox" id="confirm-checkbox" className="mt-0.5" data-testid="confirm-checkbox" />
                  I confirm this post is live, authentic, and complies with the campaign brief.
                </label>
                <div className="flex gap-2">
                  <button onClick={() => setSubmitStep(needsScreenshot() ? 3 : 2)} className="text-xs px-4 py-2 border border-white/20 text-[#9CA3AF]">Back</button>
                  <button onClick={handleSubmitPost} disabled={submitting} data-testid="final-submit-btn"
                    className="text-xs px-5 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold disabled:opacity-50">
                    {submitting ? 'Submitting...' : 'Submit Post'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Testimonial Submission Modal */}
      {testimonialModal && (() => {
        const wc = testimonialText.trim().split(/\s+/).filter(Boolean).length;
        const valid = wc >= 30 && wc <= 300;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !testimonialBusy && setTestimonialModal(null)} />
            <div className="relative glass max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto" data-testid="testimonial-modal">
              <h3 className="text-sm font-semibold mb-1">Write Your <span className="text-[#FF4343]">Testimonial</span></h3>
              <p className="text-[10px] text-[#6B7280] mb-3">For <strong className="text-[#9CA3AF]">{testimonialModal.campaign?.brand_name}</strong> · {testimonialModal.campaign?.title}</p>
              {testimonialModal.draft_feedback || testimonialModal.brand_feedback ? (
                <div className="bg-yellow-500/10 border border-yellow-500/30 p-3 mb-3">
                  <p className="text-[10px] text-yellow-400 font-mono uppercase mb-1">Revision Notes from Brand</p>
                  <p className="text-xs text-yellow-200">{testimonialModal.draft_feedback || testimonialModal.brand_feedback}</p>
                </div>
              ) : null}
              <p className="text-xs text-[#9CA3AF] mb-3 leading-relaxed">
                Write a genuine 80–200 word testimonial (30–300 word range). Mention specific results, your role, and how the product helped. Brand will approve or request revisions.
              </p>
              <div className="flex justify-end mb-2">
                <button onClick={() => aiStarter(testimonialModal.campaign_id)} data-testid="ai-testimonial-starter-btn"
                  className="text-[10px] uppercase font-mono text-[#FF4343] hover:underline">✨ AI Starter</button>
              </div>
              <textarea value={testimonialText} onChange={e => setTestimonialText(e.target.value)} rows={8} data-testid="testimonial-textarea"
                placeholder="e.g., As VP of Sales at Acme, I've used X for two years. The reporting alone saved my team..."
                className="w-full px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343] resize-none" />
              <div className="flex items-center justify-between text-[10px] text-[#6B7280] mt-2">
                <span data-testid="testimonial-word-count">{wc} words · <span className="text-[#10B981]">draft auto-saved</span></span>
                <span className={valid ? 'text-[#10B981]' : 'text-[#FF4343]'}>{valid ? 'Looks good' : '30–300 words required'}</span>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={submitTestimonial} disabled={!valid || testimonialBusy} data-testid="submit-testimonial-btn"
                  className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-5 py-2 text-sm font-semibold hover:bg-[#E03030] disabled:opacity-50 transition-all">
                  {testimonialBusy ? 'Submitting...' : 'Submit for Brand Review'}
                </button>
                <button onClick={() => setTestimonialModal(null)} disabled={testimonialBusy} className="border border-white/20 text-[#9CA3AF] hover:text-white px-5 py-2 text-sm transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Survey Submission Modal */}
      {surveyModal && (() => {
        const { survey, participation } = surveyModal;
        const required = (survey?.questions || []).filter(q => q.required);
        const allAnswered = required.every(q => String(surveyAnswers[q.id] || '').trim() !== '');
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !surveyBusy && setSurveyModal(null)} />
            <div className="relative glass max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" data-testid="survey-modal">
              <h3 className="text-sm font-semibold mb-1">{participation.campaign?.brand_name} <span className="text-[#FF4343]">Survey</span></h3>
              <p className="text-[10px] text-[#6B7280] mb-4">{participation.campaign?.title}</p>

              {survey.external_url ? (
                <div className="space-y-3">
                  <p className="text-xs text-[#9CA3AF]">This brand uses an external survey tool. Complete the survey at the link below, then click "I've Completed It" to mark this as done.</p>
                  <a href={survey.external_url} target="_blank" rel="noopener noreferrer" data-testid="survey-external-link"
                    className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#FF4343] bg-[#FF4343]/10 text-[#FF4343] text-xs font-semibold hover:bg-[#FF4343] hover:text-[#090E33] transition-all">
                    Open Survey <ExternalLink className="w-3 h-3" />
                  </a>
                  <div className="flex gap-2 pt-2">
                    <button onClick={() => submitSurvey(true)} disabled={surveyBusy} data-testid="survey-external-complete-btn"
                      className="border-2 border-[#10B981] bg-[#10B981] text-[#090E33] px-5 py-2 text-xs font-semibold hover:bg-[#059669] disabled:opacity-50 transition-all">
                      {surveyBusy ? 'Saving...' : "I've Completed It"}
                    </button>
                    <button onClick={() => setSurveyModal(null)} disabled={surveyBusy} className="border border-white/20 text-[#9CA3AF] px-5 py-2 text-xs hover:text-white">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {(survey.questions || []).map((q, qi) => (
                    <div key={q.id} className="bg-[#090E33] border border-white/10 p-4" data-testid={`survey-q-${qi}`}>
                      <label className="block text-xs text-white mb-2">
                        <span className="text-[#6B7280] mr-1">{qi + 1}.</span> {q.prompt}
                        {q.required && <span className="text-[#FF4343] ml-1">*</span>}
                      </label>
                      {q.type === 'rating' && (
                        <div className="flex gap-2">
                          {[1, 2, 3, 4, 5].map(n => (
                            <button key={n} type="button" onClick={() => setSurveyAnswers({ ...surveyAnswers, [q.id]: String(n) })}
                              data-testid={`survey-q-${qi}-rating-${n}`}
                              className={`w-9 h-9 border text-xs ${surveyAnswers[q.id] === String(n) ? 'border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold' : 'border-white/10 text-[#9CA3AF] hover:border-white/30'}`}>{n}</button>
                          ))}
                        </div>
                      )}
                      {q.type === 'multiple_choice' && (
                        <div className="space-y-1.5">
                          {(q.options || []).map((opt, oi) => (
                            <label key={oi} className="flex items-center gap-2 text-xs text-[#9CA3AF] cursor-pointer">
                              <input type="radio" name={q.id} checked={surveyAnswers[q.id] === opt}
                                onChange={() => setSurveyAnswers({ ...surveyAnswers, [q.id]: opt })}
                                data-testid={`survey-q-${qi}-opt-${oi}`} />
                              {opt}
                            </label>
                          ))}
                        </div>
                      )}
                      {q.type === 'short_text' && (
                        <input value={surveyAnswers[q.id] || ''} onChange={e => setSurveyAnswers({ ...surveyAnswers, [q.id]: e.target.value })}
                          data-testid={`survey-q-${qi}-input`}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-xs focus:outline-none focus:border-[#FF4343]" />
                      )}
                      {q.type === 'long_text' && (
                        <div>
                          <div className="flex justify-end mb-1">
                            <button onClick={() => aiSurveyStarter(survey.id, q.id)} data-testid={`survey-q-${qi}-ai-starter`}
                              className="text-[10px] uppercase font-mono text-[#FF4343] hover:underline">✨ AI Starter</button>
                          </div>
                          <textarea value={surveyAnswers[q.id] || ''} onChange={e => setSurveyAnswers({ ...surveyAnswers, [q.id]: e.target.value })}
                            rows={3} data-testid={`survey-q-${qi}-input`}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-xs focus:outline-none focus:border-[#FF4343] resize-none" />
                        </div>
                      )}
                    </div>
                  ))}
                  <div className="flex gap-2 pt-2">
                    <button onClick={() => submitSurvey(false)} disabled={!allAnswered || surveyBusy} data-testid="submit-survey-btn"
                      className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-5 py-2 text-xs font-semibold hover:bg-[#E03030] disabled:opacity-50 transition-all">
                      {surveyBusy ? 'Submitting...' : 'Submit Responses'}
                    </button>
                    <button onClick={() => setSurveyModal(null)} disabled={surveyBusy} className="border border-white/20 text-[#9CA3AF] px-5 py-2 text-xs hover:text-white">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Video Testimonial Upload Modal */}
      {videoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !videoBusy && setVideoModal(null)} />
          <div className="relative glass max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" data-testid="video-modal">
            <h3 className="text-sm font-semibold mb-1">Upload Video <span className="text-[#FF4343]">Testimonial</span></h3>
            <p className="text-[10px] text-[#6B7280] mb-4">{videoModal.campaign?.brand_name} · {videoModal.campaign?.title}</p>
            <p className="text-xs text-[#9CA3AF] mb-3">Record a 30-90 second selfie video. Mention your name, role, and a specific result.</p>
            <label className="block">
              <input type="file" accept="video/*" onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 100 * 1024 * 1024) { toast.error('Max 100MB'); return; }
                const fd = new FormData(); fd.append('file', file);
                try {
                  setVideoBusy(true);
                  const { data } = await axios.post(`${API}/upload`, fd, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
                  setVideoUrl(data.serve_url);
                  toast.success('Video uploaded');
                } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
                finally { setVideoBusy(false); }
              }} data-testid="video-file-input"
                className="block w-full text-sm text-[#6B7280] file:mr-4 file:py-2 file:px-4 file:border file:border-white/10 file:text-xs file:text-[#FF4343] file:bg-transparent hover:file:bg-[#FF4343]/10 file:cursor-pointer" />
            </label>
            {videoUrl && (
              <video src={videoUrl} controls className="w-full max-h-60 bg-black mt-3" />
            )}
            <div className="flex gap-2 mt-4">
              <button onClick={submitVideo} disabled={!videoUrl || videoBusy} data-testid="submit-video-btn"
                className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-5 py-2 text-sm font-semibold disabled:opacity-50">
                {videoBusy ? 'Working...' : 'Submit for Review'}
              </button>
              <button onClick={() => setVideoModal(null)} disabled={videoBusy} className="border border-white/20 text-[#9CA3AF] px-5 py-2 text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Session Slot Proposal Modal */}
      {sessionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !sessionBusy && setSessionModal(null)} />
          <div className="relative glass max-w-lg w-full p-6" data-testid="session-modal">
            <h3 className="text-sm font-semibold mb-1">Propose Times for <span className="text-[#FF4343]">{sessionModal.campaign?.brand_name}</span></h3>
            <p className="text-[10px] text-[#6B7280] mb-4">{sessionModal.campaign?.title}</p>
            <p className="text-xs text-[#9CA3AF] mb-3">Propose 1-3 time slots that work for you. Brand will pick one and confirm with a meeting URL.</p>
            {[0, 1, 2].map(i => (
              <div key={i} className="mb-2">
                <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Option {i + 1}{i === 0 ? ' *' : ''}</label>
                <input type="datetime-local" value={sessionSlots[i]} onChange={e => { const u = [...sessionSlots]; u[i] = e.target.value; setSessionSlots(u); }}
                  data-testid={`session-slot-${i}`} min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 16)}
                  className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
              </div>
            ))}
            <div className="flex gap-2 mt-4">
              <button onClick={submitSessionSlots} disabled={sessionBusy || !sessionSlots[0]} data-testid="submit-session-slots-btn"
                className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-5 py-2 text-sm font-semibold disabled:opacity-50">{sessionBusy ? 'Sending...' : 'Send Proposed Times'}</button>
              <button onClick={() => setSessionModal(null)} disabled={sessionBusy} className="border border-white/20 text-[#9CA3AF] px-5 py-2 text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Session Complete (Notes) Modal */}
      {sessionNotesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !sessionBusy && setSessionNotesModal(null)} />
          <div className="relative glass max-w-lg w-full p-6" data-testid="session-notes-modal">
            <h3 className="text-sm font-semibold mb-1">Post-Session <span className="text-[#FF4343]">Notes</span></h3>
            <p className="text-[10px] text-[#6B7280] mb-4">{sessionNotesModal.participation.campaign?.title}</p>
            <p className="text-xs text-[#9CA3AF] mb-3">Summarize what was discussed, key feedback, and any next steps (min 30 chars).</p>
            <textarea value={sessionNotes} onChange={e => setSessionNotes(e.target.value)} rows={6} data-testid="session-notes-textarea"
              placeholder="e.g., Walked through the new dashboard. Strong reaction to the analytics filter. Suggested adding..."
              className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343] resize-none" />
            <p className="text-[10px] text-[#6B7280] mt-1 text-right">{sessionNotes.length} chars</p>
            <div className="flex gap-2 mt-3">
              <button onClick={submitSessionComplete} disabled={sessionBusy || sessionNotes.trim().length < 30} data-testid="submit-session-complete-btn"
                className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-5 py-2 text-sm font-semibold disabled:opacity-50">{sessionBusy ? 'Saving...' : 'Mark Complete'}</button>
              <button onClick={() => setSessionNotesModal(null)} disabled={sessionBusy} className="border border-white/20 text-[#9CA3AF] px-5 py-2 text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Project Milestones Modal */}
      {projectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setProjectModal(null)} />
          <div className="relative glass max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" data-testid="project-modal">
            <h3 className="text-sm font-semibold mb-1">{projectModal.participation.campaign?.brand_name} <span className="text-[#FF4343]">Project</span></h3>
            <p className="text-[10px] text-[#6B7280] mb-3">{projectModal.participation.campaign?.title}</p>
            <div className="bg-white/5 p-3 mb-4 border-l-2 border-[#FF4343]">
              <p className="text-[10px] text-[#6B7280] uppercase font-mono mb-1">Scope</p>
              <p className="text-xs text-[#9CA3AF] whitespace-pre-wrap">{projectModal.project.scope}</p>
            </div>
            <div className="space-y-3">
              {projectModal.project.milestones.map((m, mi) => {
                const editing = milestoneSubmit[m.id] || { url: '', notes: '' };
                return (
                  <div key={m.id} className="bg-[#090E33] border border-white/10 p-3" data-testid={`milestone-${m.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-white">M{mi + 1}: {m.title}</span>
                      <span className="text-[10px] font-mono text-[#FF4343]">${m.amount?.toLocaleString()} · {m.status}</span>
                    </div>
                    {m.description && <p className="text-xs text-[#9CA3AF] mb-2">{m.description}</p>}
                    {m.due_date && <p className="text-[10px] text-[#6B7280] mb-2">Due: {m.due_date}</p>}

                    {m.status === 'approved' ? (
                      <p className="text-[10px] text-[#10B981] font-mono">✓ Approved · Payout: {m.payout_status}</p>
                    ) : m.status === 'submitted' ? (
                      <p className="text-[10px] text-yellow-400 font-mono">Awaiting brand review</p>
                    ) : (
                      <div className="space-y-2 mt-2">
                        {m.status === 'revision_requested' && m.brand_feedback && (
                          <p className="text-[10px] text-yellow-400">Revision notes: {m.brand_feedback}</p>
                        )}
                        <input value={editing.url} onChange={e => setMilestoneSubmit({ ...milestoneSubmit, [m.id]: { ...editing, url: e.target.value } })}
                          data-testid={`milestone-${m.id}-url`}
                          placeholder="Deliverable URL (Google Doc / Figma / GitHub PR…)"
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-xs focus:outline-none focus:border-[#FF4343]" />
                        <textarea value={editing.notes} onChange={e => setMilestoneSubmit({ ...milestoneSubmit, [m.id]: { ...editing, notes: e.target.value } })}
                          rows={2} data-testid={`milestone-${m.id}-notes`}
                          placeholder="Notes on the deliverable..."
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-xs focus:outline-none focus:border-[#FF4343] resize-none" />
                        <button onClick={() => submitMilestone(m.id)} disabled={projectBusy === m.id} data-testid={`submit-milestone-${m.id}`}
                          className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-4 py-1.5 text-xs font-semibold disabled:opacity-50">
                          {projectBusy === m.id ? 'Submitting...' : 'Submit for Review'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <button onClick={() => setProjectModal(null)} className="mt-4 border border-white/20 text-[#9CA3AF] px-5 py-2 text-sm hover:text-white">Close</button>
          </div>
        </div>
      )}

      {/* AI Training Q&A Modal */}
      {aiTrainingModal && (() => {
        const { aitr, participation } = aiTrainingModal;
        const questions = aitr?.questions || [];
        const allMet = questions.every(q => {
          const ans = (aiTrainingAnswers[q.id] || '').trim();
          return ans.split(/\s+/).filter(Boolean).length >= (q.min_answer_words || 30);
        });
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !aiTrainingBusy && setAiTrainingModal(null)} />
            <div className="relative glass max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" data-testid="ai-training-modal">
              <h3 className="text-sm font-semibold mb-1">Train <span className="text-[#FF4343]">{participation.campaign?.brand_name}'s AI</span></h3>
              <p className="text-[10px] text-[#6B7280] mb-3">{participation.campaign?.title} · drafts auto-save</p>
              <p className="text-xs text-[#9CA3AF] mb-4 leading-relaxed">Answer each question in your own voice — depth and specifics matter more than length. Your answers will be used as fine-tuning data for the brand's Claude/OpenAI instance.</p>
              <div className="space-y-4">
                {questions.map((q, qi) => {
                  const ans = aiTrainingAnswers[q.id] || '';
                  const wc = ans.trim().split(/\s+/).filter(Boolean).length;
                  const minW = q.min_answer_words || 30;
                  const ok = wc >= minW;
                  return (
                    <div key={q.id} className="bg-[#090E33] border border-white/10 p-4" data-testid={`ai-training-q-${qi}`}>
                      <div className="flex items-start justify-between mb-1 gap-2">
                        <p className="text-xs text-white"><span className="text-[#6B7280] mr-1">Q{qi + 1}.</span> {q.prompt}</p>
                        {q.category && <span className="text-[9px] font-mono uppercase text-[#FF4343] bg-[#FF4343]/10 px-1.5 py-0.5 whitespace-nowrap">{q.category}</span>}
                      </div>
                      {q.context && <p className="text-[10px] text-[#6B7280] italic mb-2">Context: {q.context}</p>}
                      <textarea value={ans} onChange={e => setAiTrainingAnswers({ ...aiTrainingAnswers, [q.id]: e.target.value })}
                        rows={5} data-testid={`ai-training-q-${qi}-answer`}
                        placeholder="Answer in your own voice…"
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-xs focus:outline-none focus:border-[#FF4343] resize-none" />
                      <div className="flex items-center justify-between text-[10px] mt-1">
                        <span className="text-[#6B7280]">{wc} words · <span className="text-[#10B981]">auto-saved</span></span>
                        <span className={ok ? 'text-[#10B981]' : 'text-[#FF4343]'}>{ok ? '✓ ready' : `Need ≥${minW} words`}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2 mt-4 sticky bottom-0 bg-[#090E33] pt-3">
                <button onClick={submitAiTraining} disabled={!allMet || aiTrainingBusy} data-testid="submit-ai-training-btn"
                  className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-5 py-2 text-sm font-semibold disabled:opacity-50">
                  {aiTrainingBusy ? 'Submitting…' : `Submit All ${questions.length} Answers`}
                </button>
                <button onClick={() => setAiTrainingModal(null)} disabled={aiTrainingBusy} className="border border-white/20 text-[#9CA3AF] px-5 py-2 text-sm">Save &amp; Close</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}


